/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { Injectable, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';
import { AsideService } from 'app/layout/components/aside-container/aside.service';
import { FormMode } from 'app/shared/enumeration/form-mode';
import { logValidationErrorsForDevAndTest } from 'app/shared/helpers/form-validation-helpers';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';
import { ApiResponseSummary } from 'app/shared/models/api-info.model';
import { ConfirmationDialogService } from 'app/shared/services/confirmation-dialog.service';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { filter, take } from 'rxjs';

@Injectable()
export abstract class CreateUpdateBaseComponent<T> implements OnInit {
	@BlockUI() blockUI: NgBlockUI;

	public data: any;
	protected asideMessage = 'update';
	protected formMode: FormMode;
	protected concurrencyErrorMessage: string;
	protected formHasAsyncValidators: boolean = false;

	public viewModel: T;

	public dataForm: FormGroup;

	protected useBlockUi = true;

	constructor(
		protected fb: FormBuilder,
		protected asideService: AsideService,
		protected formHelperService: ReactiveFormHelperService,
		protected confirmationDialogService: ConfirmationDialogService,
	) {}

	// create the form group used for the reactiveform
	protected abstract createFormGroup(): FormGroup;
	// populate the controls when the form is initially loaded
	protected abstract populateControls(datafrom: FormGroup);
	// create the update model when the form is to be saved
	protected abstract createUpdateModel();
	// load the data for intial population of the form
	protected abstract loadData(id: number): Promise<T>;
	// perform the update with the created data model
	protected abstract doUpdate(id: number, dataToUpdate: any): Promise<ApiResponseSummary<any>>;
	// perform the create with the created data model
	protected abstract doCreate(dataToUpdate: any);

	protected processData() {}

	ngOnInit() {
		this.formMode = this.data?.formMode;
		this.processData();

		this.dataForm = this.createFormGroup();

		this.loadFormDetails();
	}
	get isCreateMode(): boolean {
		return this.data.formMode === FormMode.Create;
	}
	get isUpdateMode(): boolean {
		return this.data.formMode === FormMode.Update;
	}
	get isDuplicateMode(): boolean {
		return this.data.formMode === FormMode.Duplicate;
	}

	protected get performUpdate(): boolean {
		return this.formMode == FormMode.Update;
	}

	public get saveText(): string {
		switch (this.formMode) {
			case FormMode.Create:
				return this.createText;
			case FormMode.Update:
				return this.updateText;
			case FormMode.Duplicate:
				return this.duplicateText;
		}
	}

	public get titleText(): string {
		switch (this.formMode) {
			case FormMode.Create:
				return this.createTitleText;
			case FormMode.Update:
				return this.updateText;
			case FormMode.Duplicate:
				return this.duplicateText;
		}
	}

	protected get createText(): string {
		return 'Create';
	}
	protected get updateText(): string {
		return 'Update';
	}
	protected get duplicateText(): string {
		return 'Copy';
	}
	protected get createTitleText(): string {
		return 'Create';
	}

	loadFormDetails() {
		const id: number = this.data.itemId ? this.data.itemId : null;
		this.loadData(id).then((response: T) => {
			this.viewModel = response;
			if (this.viewModel) {
				this.initialiseForm();
			}
		});
	}

	initialiseForm() {
		this.concurrencyErrorMessage = '';
		this.populateControls(this.dataForm);
		this.dataForm.markAsPristine();
	}

	save(closeAside: boolean = true) {
		if (this.formHasAsyncValidators) {
			// If the form has async validators, we need to wait for them to complete before proceeding
			this.waitForPendingFormStatusChange();
		}

		if (this.formHelperService.isFormValid(this.dataForm)) {
			const updateModel = this.createUpdateModel();
			this.startBlockUI();
			if (this.performUpdate) {
				this.doUpdate(this.data.itemId, updateModel)
					.then((response: ApiResponseSummary<any>) => {
						this.stopBlockUI();
						if (response.success === true) {
							if (closeAside) this.asideService.close(this.asideMessage);
						} else {
							this.concurrencyErrorMessage = response.message;
						}
					})
					.catch((err) => {
						this.stopBlockUI();
						throw err;
					});
			} else {
				this.doCreate(updateModel)
					.then(() => {
						this.stopBlockUI();
						if (closeAside) this.asideService.close(this.asideMessage);
					})
					.catch((err) => {
						this.stopBlockUI();
						throw err;
					});
			}
		} else {
			logValidationErrorsForDevAndTest(this.dataForm);
		}
	}

	getFormValidationErrors() {
		Object.keys(this.dataForm.controls).forEach((key) => {
			const controlErrors: ValidationErrors = this.dataForm.get(key).errors;
			if (controlErrors != null) {
				Object.keys(controlErrors).forEach((keyError) => {
					console.debug('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				});
			}
		});
	}

	cancel() {
		var dataForm = this.dataForm;
		if (this.formHelperService.isFormDirty(dataForm)) {
			this.confirmationDialogService.openConfirmationDialogForFormLostChanges(() => {
				this.asideService.close('cancel');
			});
		} else {
			this.asideService.close('cancel');
		}
	}

	waitForPendingFormStatusChange() {
		if (this.dataForm.pending) {
			this.dataForm.statusChanges
				.pipe(
					// Wait for the async validation to finish
					filter((status) => status !== 'PENDING'),
					take(1),
				)
				.subscribe(() => this.save());
			return;
		}
	}

	startBlockUI() {
		if (this.useBlockUi) {
			this.blockUI.start();
		}
	}

	stopBlockUI() {
		if (this.useBlockUi) {
			this.blockUI.stop();
		}
	}
}
